<template>
  <div class="container">
    <oie-product-filter-bar
      :use-compare="false"
      :current-count="products.length"
      :show-sort-order="false"
      :total-count="totalCount"
      :show-facet-filter-toggle="enableFilters"></oie-product-filter-bar>

    <div v-if="totalCount === 0 && searchQuery" class="product-list__no-result">
      <p class="product-list__no-result-title">
        {{ tStatic('noListResultTitle') }}
      </p>
      <p class="product-list__no-result-text">
        {{ tStatic('noListResultText') }}
      </p>

      <div class="product-list__no-result-contact">
        {{ tStatic('searchAgainDescription') }}
      </div>

      <div class="product-list__cai-divider" role="separator" aria-hidden="true">
        <span>or get instant help</span>
      </div>

      <div class="product-list__cai-banner">
        <div class="product-list__cai-content">
          <div class="product-list__cai-icon" aria-hidden="true">
            <Chat width="24" height="24"></Chat>
          </div>
          <div class="product-list__cai-text">
            <strong>{{ tStatic("askCaiForSupportTitle") }}</strong>
            <span>{{ tStatic("askCaiForSupportText") }}</span>
          </div>
        </div>
        <button
          class="product-list__cai-btn"
          type="button"
          :disabled="isCaiPending"
          @click="openChatbot">
          {{ tStatic("askCaiForSupportButtonText") }}
          <ArrowRight width="16" height="16" aria-hidden="true"></ArrowRight>
        </button>
      </div>
    </div>

    <div v-else :class="['product-list__products-filter-wrapper', 'section-block--create-space-top']">
      <slot name="filters"></slot>

      <oie-section-block-shell :css-classes="['section-block--create-space', 'section-block--no-padding']">
        <template v-if="useSplitList">
          <h2>{{ t('vehicleRelatedProductsTitle') }}</h2>
          <p>{{ t('vehicleRelatedProductsText') }}</p>
          <oie-product-list-search
            v-if="loading || vehicleRelatedProducts.length > 0"
            :products="vehicleRelatedProducts"
            :use-list-view="useListViewComputed"
            :loading="loading"
            :type-of-listing="typeOfListing"></oie-product-list-search>
          <div v-if="!loading && vehicleRelatedProducts.length === 0" class="product-list__no-result">
            <p class="product-list__no-result-text">
              {{ t('vehicleRelatedProductsEmptyText') }}
            </p>
          </div>
          <h2>{{ t('universalProductsTitle') }}</h2>
          <p>{{ t('universalProductsText') }}</p>
          <oie-product-list-search
            v-if="loading || universalProducts.length > 0"
            :products="universalProducts"
            :use-list-view="useListViewComputed"
            :loading="loading"
            :type-of-listing="typeOfListing"></oie-product-list-search>

          <div v-if="!loading && universalProducts.length === 0" class="product-list__no-result">
            <p class="product-list__no-result-text">
              {{ t('universalProductsEmptyText') }}
            </p>
          </div>
        </template>

        <template v-else>
          <oie-product-list-search
            :products="products"
            :total-count="totalCount"
            :use-list-view="useListViewComputed"
            :loading="loading"
            :type-of-listing="typeOfListing"></oie-product-list-search>

          <oie-base-button
            v-if="(totalCount > count)"
            :css-classes="['button--blue-no-border', 'button-link', 'product-list__button', 'product-list__load-more', {'product-list__isloading': loading} ]"
            :text="tStatic('showMoreLink')"
            :disabled="loading"
            @click.native="loadMore(allFilterParameters)"></oie-base-button>
        </template>
      </oie-section-block-shell>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import httpService from '@/core/http-service'
import _ from 'lodash'
import priceService from '@/core/price-service'
import utils from '@/core/utils'
import { selectedFilters, allFilterParameters, selectedVehicle } from '@/store/filters'

export default {
  props: {
    productIds: {
      type: Array,
      default: null
    },
    typeOfListing: {
      type: String,
      default: ''
    },
    enableFilters: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    technicalInformation: [],
    filters: [],
    productExpanded: null,
    products: [],
    page: 0,
    totalCount: 0,
    count: 0,
    madeReset: false,
    searchQuery: '',
    loading: false,
    showFilter: true,
    pageSize: 40,
    isCaiPending: false
  }),
  computed: {
    ...mapGetters('ui', ['useListView', 'forceListView', 'forceGridView', 'showTechnicalnformation', 'useVat', 'isTechnicalInformationBlockActive']),
    ...mapGetters('productVariants', ['isExpanded', 'displayAllVariants']),
    useListViewComputed() {
      if (this.forceListView) {
        return true
      }
      if (this.forceGridView) {
        return false
      }
      return this.useListView
    },
    selectedFilters() {
      return selectedFilters()
    },
    allFilterParameters() {
      return allFilterParameters()
    },
    useSplitList() {
      return selectedVehicle()
    },
    vehicleRelatedProducts() {
      return this.useSplitList ? _.filter(this.products, p => !p.isUniversal) : []
    },
    universalProducts() {
      return this.useSplitList ? _.filter(this.products, p => p.isUniversal) : []
    }
  },
  watch: {
    products(value) {
      this.publish('product-list-page-wrapper.products-updated', value)
    },
    useVat: {
      immediate: true,
      handler() {
        const listProductIds = _.flatMap(this.products, 'id')
        const replacementProductIds = _.flatMap(_.flatMap(this.products, p => _.flatMap(p.replacementProducts)), 'id')
        const productIds = _.uniq([...listProductIds, ...replacementProductIds])

        if (productIds && productIds.length) {
          httpService.post('productListPrices', { productId: productIds }).then((response) => {
            priceService.populatePricesFromBackend(response.data, this.products)
          })
        }
      }
    },
    'clientModel.useListPrice': {
      immediate: true,
      handler() {
        const listProductIds = _.flatMap(this.products, 'id')
        const replacementProductIds = _.flatMap(_.flatMap(this.products, p => _.flatMap(p.replacementProducts)), 'id')
        const productIds = _.uniq([...listProductIds, ...replacementProductIds])
        if (productIds && productIds.length) {
          httpService.post('productListPrices', { productId: productIds }).then((response) => {
            priceService.populatePricesFromBackend(response.data, this.products)
          })
        }
      }
    },
    productIds() {
      this.loading = true
      this.page = 0
      this.products = []
      this.loadMore()
    }
  },
  async mounted() {
    this.page = (parseInt(utils.getQueryStringParam('p')) - 1) || 0

    this.searchQuery = utils.getQueryStringParam('query') || ''

    await this.loadMore()

    this.subscribe('dropdown-option-selected-sort-products', selectedOption => {
      this.products = []
      this.page = 0
      this.sortBy = selectedOption.value
    })

    this.subscribe('toggle.articles', (id) => {
      this.productExpanded = id
    })

    this.subscribe('chat.response.complete', () => {
      this.isCaiPending = false
    })
  },
  methods: {
    ...mapActions('productVariants', ['openAllVariants', 'toggleShowAllVariants']),
    openChatbot() {
      this.isCaiPending = true
      const message = this.tStatic('askCaiForSupportMessageTemplate').replace('{search_term}', this.searchQuery)
      this.publish('chat.open', message)
    },
    getLoadMoreBody(filters) {
      const defaultBody = {
        page: this.page,
        pageSize: this.getProductListPageSize(this.pageSize),
        query: this.searchQuery,
        ...filters
      }

      _.merge(defaultBody, {
        vehicleFilter: utils.getQueryStringParam('vehicleFilter'),
        categoryFilter: utils.getQueryStringParam('categoryFilter')
      })

      defaultBody.productIds = this.productIds

      return defaultBody
    },
    setProductListPrices(productIds) {
      httpService.post('productListPrices', { productId: productIds }).then((response) => {
        priceService.populatePricesFromBackend(response.data, this.products)
      })
    },
    async loadMore(filters = {}) {
      // Include stock debug query params if present (used only by CmsAdmins for testing)
      const queryParams = new URLSearchParams()
      const currentStock = utils.getQueryStringParam('currentStock')
      const isOrderItem = utils.getQueryStringParam('isOrderItem')
      const deliveryTime = utils.getQueryStringParam('deliveryTime')
      const deliveryDate = utils.getQueryStringParam('deliveryDate')

      if (currentStock) queryParams.append('currentStock', currentStock)
      if (isOrderItem) queryParams.append('isOrderItem', isOrderItem)
      if (deliveryTime) queryParams.append('deliveryTime', deliveryTime)
      if (deliveryDate) queryParams.append('deliveryDate', deliveryDate)

      const stockParams = queryParams.toString() ? `?${queryParams.toString()}` : ''

      try {
        this.page++
        const body = this.getLoadMoreBody(filters)

        console.log('body', body)

        this.loading = true
        const response = await httpService.post('products' + stockParams, body)
        this.totalCount = response.data.totalCount
        this.products = this.products.concat(response.data.data)
        this.count = this.products.length
        const listProductIds = _.flatMap(response.data.data, 'id')
        const replacementProductIds = _.flatMap(_.flatMap(response.data.data, p => _.flatMap(p.replacementProducts)), 'id')
        const productIds = _.uniq([...listProductIds, ...replacementProductIds])
        this.setProductListPrices(productIds)
        this.loading = false

        const allProductsWithVariants = _.chain(this.products).filter(product => product.items.length > 1).map(p => p.id).value()
        this.openAllVariants(allProductsWithVariants)
      } catch (e) {
        // do nothing
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss">
.product-list__no-result-contact {
  margin-top: 0.5rem;
}

.product-list__cai-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1.25rem 0 1rem;
  color: #757272;
  font-size: 0.875rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: #ccc;
  }

  span {
    padding: 0 0.75rem;
  }
}

.product-list__cai-banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background-color: #eff6fb;
  border-radius: 0.5rem;
  padding: 0.875rem 1rem;
  width: 100%;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
}

.product-list__cai-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.product-list__cai-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: #006db6;
  color: #fff;
}

.product-list__cai-text {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;

  strong {
    font-size: 1rem;
  }

  span {
    font-size: 1rem;
    color: #006DB6;
  }
}

.product-list__cai-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
  padding: 0.5rem 1rem;
  height: 2.5rem;
  background-color: #fff;
  border: 1px solid #006db6;
  border-radius: 0.1875rem;
  font-size: 1rem;
  font-weight: 600;
  color: #006db6;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #dfebf3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.product-list__load-more {
  margin-top: 1rem;
}
.product-list__isloading {

}
</style>
